<template>
  <div id="review" class="header-padding bg-light-grey">
    <header class="page-header border-bottom ff ff-center horizontal-padding section-padding">
      <h1 style="margin-bottom: 0;">Review</h1>
      <p>Below is a summary of your estimates. From here, you can export them as a pdf or pre-filled request form by clicking <strong><em>Next</em></strong>.</p>
    </header>
    
    <section role="content" id="review-content" class="horizontal-padding section-padding max-width">
      
      <article v-for="(estimate, e) in estimates" class="review-estimate" :key="estimate.uid">
        <div class="ff-columns">
          <div class="ff-12 ff-lg-8">
            <div class="review-top">
              <div class="review-top-heading">
                <h2 class="cyan">{{ estimate.name }}</h2> 
                <div class="review-delete-btn" v-on:click="deleteEstimate(e)">
                  <icon name="icon-bin" color="grey" size=18 />
                </div>
              </div>
            </div>
            
            <div class="bg-black summary-padding">
              <div class="summary-header">
                <h3 class="white">Summary</h3>
                <div class="chip">
                  Estimate #{{ estimate.estimateNo }}
                </div>
              </div>
            </div>
            
            <ul class="review-tests">
              <li 
                v-for="(test, t) in estimate.tests.optimal" 
                :class="test.test_type | testClass"
                :key="'test-' + estimate.uid + '-' + test.index"
              >
                <div class="review-test ff ff-nowrap">
                  <router-link v-if="test.test_type === 2" :to="test.url" class="ff review-test-name">
                    <strong v-html="test.name"></strong>
                  </router-link>
                  
                  <div v-if="test.test_type < 2" class="ff review-test-name" v-on:click="toggleOpenTests(estimate.uid,test.index)">
                    <div><strong v-if="test.test_type === 0">Package:</strong><strong v-if="test.test_type === 1">Combination:</strong> <span v-html="test.name"></span></div>
                    <div v-if="test.test_type === 0 && test.short_description" class="review-test-desc ff-hide ff-sm-hide">
                      <span>{{ test.short_description }}</span>
                    </div>
                  </div>
                  
                  <router-link v-if="test.test_type === 2" :to="test.url" class="ff review-test-price">
                    <span>${{ test.price }} <em>(ex GST)</em></span>
                  </router-link>
                  
                  <div v-if="test.test_type < 2" class="ff review-test-price" v-on:click="toggleOpenTests(estimate.uid,test.index)">
                    <span>${{ test.price }} <em>(ex GST)</em></span>
                    <s v-if="estimate.tests.children[t]">${{ estimate.tests.children[t] | originalPrice }} <em>(ex GST)</em></s>
                  </div>
                  
                  <div class="ff ff-nowrap review-test-actions">

                    <div class="review-test-remove" v-on:click="deleteTest(test.index,e)">
                      <div class="ff"><icon name="ui-cross" color="white" size=18 /></div>
                    </div>

                    <div 
                      v-if="test.test_type < 2 && test.children.length > 0" 
                      v-on:click="toggleOpenTests(estimate.uid,test.index)" 
                      class="review-test-toggle-children"
                      :class="openTests.indexOf(estimate.uid+'-'+test.index) !== -1 ? 'open' : ''"
                    >
                      <div class="ff"><icon name="ui-chevron-down" color="white" size=18 /></div>
                    </div>

                    <router-link 
                      v-if="test.test_type === 2 || ( test.test_type === 0 && test.children.length === 0 )" 
                      :to="test.url" 
                      class="review-test-arrow"
                    >
                      <div class="ff"><icon name="ui-chevron-right" color="white" size=18 /></div>
                    </router-link>

                  </div>
                </div>
                <transition name="slide-fade">
                  <ul 
                    v-if="estimate.tests.children[t] && openTests.indexOf(estimate.uid+'-'+test.index) !== -1" 
                    class="review-child-tests"
                  >
                    <li 
                      v-for="childtest in estimate.tests.children[t]"
                      :key="'child-test-' + estimate.uid + '-' + childtest.index"
                    >
                      <div class="review-test ff ff-nowrap">
                        <router-link :to="childtest.url" class="ff review-test-name">
                          <strong v-html="childtest.name"></strong>
                        </router-link>
                        
                        <router-link :to="childtest.url" class="ff review-test-price">
                          <s>${{ childtest.price }} <em>(ex GST)</em></s>
                        </router-link>
                        
                        <div class="ff ff-nowrap review-test-actions">
                          <div class="review-test-remove" v-on:click="deleteTest(childtest.index,e)">
                            <div class="ff"><icon name="ui-cross" color="white" size=18 /></div>
                          </div>
                          <router-link :to="childtest.url" class="review-test-arrow">
                            <div class="ff"><icon name="ui-chevron-right" color="white" size=18 /></div>
                          </router-link>
                        </div>
                      </div>
                    </li>
                  </ul>
                </transition>
              </li>
              <li 
                v-for="test in estimate.tests.dependents.list" 
                class="option"
                :key="'dependent-' + estimate.uid + '-' + test.index"
              >
                <div class="review-dependent ff ff-nowrap">
                  <router-link :to="test.url" class="ff review-test-name">
                    <div>
                      <strong v-html="test.name"></strong> <strong v-if="estimate.tests.dependents.active.indexOf(test.index) === -1" class="cyan">+${{ test.price }} <em>(ex GST)</em></strong>
                    </div>
                  </router-link>
                  
                  <div 
                    class="review-dependent-option-toggle ff ff-center ff-nowrap bg-black"
                    :class="estimate.tests.dependents.active.indexOf(test.index) !== -1 ? 'active' : 'inactive'"
                  >
                    <div class="review-dependent-option review-dependent-option-true ff ff-center" v-on:click="addTest(test.index,e)"><h5>Yes</h5></div>
                    <div class="review-dependent-option review-dependent-option-false ff ff-center" v-on:click="deleteTest(test.index,e)"><h5>No</h5></div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="ff-12 ff-lg-4">
            <div class="review-totals ff ff-ttb">
              <h5 class="grey review-totals-title bg-light-grey">Total</h5>
              <div class="review-totals-price ff ff-align-between ff-valign-center ff-nowrap">
                <div class="review-totals-key">
                  Price per sample
                </div>
                <div class="review-totals-value">
                  ${{ estimate.price }} <em>(ex GST)</em>
                </div>
              </div>
              <div class="review-totals-qty ff ff-align-between ff-valign-center ff-nowrap">
                <div class="review-totals-key">
                  Total samples
                </div>
                <div class="review-totals-value">
                  <div class="review-totals-qty-selector ff ff-center ff-nowrap bg-black white">
                    <div class="review-totals-qty-btn" 
                      :class="estimate.qty === 1 ? 'min' : ''" 
                      v-on:click="updateQty((parseInt(estimate.qty) - 1),e)"
                    >
                      <icon name="ui-minus" color="cyan" size=20 />
                    </div>
                    
                    <div class="review-totals-qty-total">
                      <input 
                        :value="estimate.qty" 
                        @keydown="numericOnly()"
                        @input="updateQty($event.target.value,e)" 
                        @blur="checkQty($event.target.value,e)"
                        type="text"  
                        name="sample-qty" 
                        id="sample-qty" 
                        :ref="'sampleQty'+e"
                      >
                    </div>
                    
                    <div 
                      class="review-totals-qty-btn" 
                      v-on:click="updateQty((parseInt(estimate.qty) + 1),e)"
                    >
                      <icon name="ui-plus" color="cyan" size=20 />
                    </div>
                  </div>
                </div>
              </div>
              <div class="review-totals-tally ff ff-ttb ff-center">
                <div class="calc-totals-tally-total cyan">
                  ${{ estimate.total }}
                </div>
                <div class="review-totals-tally-qty cyan">
                  <em>(ex GST)</em>
                  <br>
                  <strong class="black">${{ (estimate.total*1.1).toFixed(2) }} <em>(inc GST)</em></strong>
                  <br>
                  <span v-if="estimate.qty === 1" class="grey">for 1 sample</span>
                  <span v-else class="grey">for {{ estimate.qty }} samples</span>
                  
                </div>
                <div v-if="estimate.totalLoss" class="calc-totals-tally-loss ff ff-center">
                  <div><icon name="ui-warning" color="pink" size=30 /></div>
                  <em>The minimum charge per sample is <strong>${{ $store.state.global.minimum_price.toFixed(2) }} (ex GST)</strong>.</em>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </article>
      
    </section>
  </div>
</template>

<script>

// @ is an alias to /src
export default {
  name: "Review",
  data() {
    return {
      openTests: []
    }
  },
  computed: {
    apiURL() {
      return this.$store.state.apiURL;
    },
    estimates() {
      var estimates = [];
      var storedTests = this.$store.state.tests;
      var storedEst = this.$store.state.calculator.estimates;
      
      if(storedEst.length > 0){ 
        for(var i = 0; i < storedEst.length; i++) {
          
          var sampleType = this.$store.state.sampleTypes.filter(s => s.name === storedEst[i].displayedType);
          var samplePrice = parseFloat(storedEst[i].samplePrice);
          var categoryLink = sampleType.length > 0 ? '/tests/'+sampleType[0].slug : '';
          var totalSavings = false;
          var totalLoss = false;
          
          if(samplePrice < this.$store.state.global.minimum_price) {
            totalLoss = (this.$store.state.global.minimum_price - samplePrice).toFixed(2);
            samplePrice = this.$store.state.global.minimum_price;
          }
          
          if(!totalLoss && storedEst[i].sampleSaving) {
            totalSavings = (storedEst[i].sampleSaving * storedEst[i].sampleQty).toFixed(2);
          }
          
          var optimal = storedEst[i].tests.optimal.filter(t => t.test_type <= 2);
          var matches = storedEst[i].tests.matches;
          var dependentsList = [];
          var dependentsActive = [];
          
          if(storedEst[i].tests.individual.length > 0) {
            storedEst[i].tests.individual.forEach(function(parentTest){
                  
              if(parentTest.children.length > 0) {
                var childTests = storedTests.filter(c => parentTest.children.indexOf(c.test_id) !== -1 && c.test_type === 3);
                
                childTests.forEach(function(ct){
                   
                  dependentsList.push(ct);
                  
                  if(storedEst[i].tests.individual.some(s => s.index === ct.index)) {
                    dependentsActive.push(ct.index);
                  }
                  
                });
              }
              
            });
          }
          
          estimates.push({
            uid: storedEst[i].uid,
            estimateNo: storedEst[i].estimateNo,
            name: storedEst[i].displayedType + ' Estimate (' + (i+1) + ')',
            type: storedEst[i].sampleType,
            categoryLink: categoryLink,
            qty: storedEst[i].sampleQty,
            price: samplePrice.toFixed(2),
            total: (samplePrice * storedEst[i].sampleQty).toFixed(2),
            totalSavings: totalSavings,
            totalLoss: totalLoss,
            tests: {
              optimal: optimal,
              children: matches,
              dependents: {
                list: dependentsList,
                active: dependentsActive
              }
            }
          });
          
        }
      }
      
      return estimates;
    },
    meta() {
      var meta = {
        title: "Review",
        url: "https://www.csbplab.com.au/estimates/review",
        desc: "",
        image: ""
      }
      
      return meta;
    }
  },
  beforeMount() {
    
  },
  mounted() {
    
  },
  methods: {
    updateQty(newQty,estimate) {
      var currentQty = this.$store.state.calculator.estimates[estimate].sampleQty;
        
      if(newQty !== '') {
        
        if(isNaN(newQty)) {
        
          newQty = currentQty;
          
        } else {
          
          if(newQty < 1) {
            newQty = 1;
          }
          
        }
        
        this.$store.commit("updateEstimate",{
          estimate: estimate,
          sampleQty: parseInt(newQty)
        });
      }
    },
    checkQty(newQty,estimate) {
      if(!newQty) {
        this.$store.commit("updateEstimate",{
          estimate: estimate,
          sampleQty: 1
        });
        
        this.$refs['sampleQty'+estimate].value = 1;
      }
    },
    numericOnly(e) {
      var charCode = (e.which) ? e.which : e.keyCode;
      
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        e.preventDefault();
      }
    },
    deleteEstimate(estimate) {
      this.$gtm.trackEvent({
        'event': 'review',
        'action': 'click',
        'trash-icon': 'delete estimate'
      });
      
      if(this.$store.state.calculator.currentEstimate === estimate) {
        if((estimate-1) >= 0) {
          this.$store.commit("setCurrentEstimate",estimate-1);
        } else {
          this.$store.commit("setCurrentEstimate",0);
        }
      }
      
      this.$store.commit("deleteEstimate",estimate);
    },
    addTest(testIndex, estimateIndex) {
      var alreadyExists = false;
      
      // Make sure that the estimate doesn't have it yet.
      var tests = this.$store.state.calculator.estimates[estimateIndex].tests;
      
      if(tests.individual.some(t => t.index === testIndex) || tests.optimal.some(t => t.index === testIndex)) {
        alreadyExists = true;
      }
      
      if(!alreadyExists){
        this.$store.dispatch("addToEstimate", {
          test: testIndex,
          estimate: estimateIndex
        });
      }
    },
    deleteTest(testIndex, estimateIndex) {
      this.$store.dispatch("deleteFromEstimate", {
        test: testIndex,
        estimate: estimateIndex
      });
    },
    toggleOpenTests(e,t) {
      
      if(this.openTests.indexOf(e+'-'+t) !== -1) {
        this.openTests.splice(this.openTests.indexOf(e+'-'+t), 1);
      } else {
        this.openTests.push(e+'-'+t);
      }
      
    }
  },
  metaInfo () {
    return {
      title: this.meta.title,
      link: [
        {
          rel: "canonical",
          href: this.meta.url
        }
      ],
      meta: [
        {
          name: "description",
          content: this.meta.desc
        },
        {
          property: "og:title",
          content: this.meta.title + " | CSBP Labs="
        },
        {
          property: "og:url",
          content: this.meta.url
        },
        {
          property: "og:image",
          content: this.meta.image
        },
        {
          property: "og:description",
          content: this.meta.desc
        },
        {
          property: "twitter:title",
          content: this.meta.title + " | CSBP Lab"
        },
        {
          property: "twitter:site",
          content: this.meta.url
        },
        {
          property: "twitter:image.src",
          content: this.meta.image
        },
        {
          property: "twitter:description",
          content: this.meta.desc
        }
      ]
    }
  },
  filters: {
    testClass(val) {
      var testClass = '';
      
      switch(val) {
        case 0:
          testClass = 'package';
          break;
        case 1:
          testClass = 'combination';
          break;
        case 2:
          testClass = 'individual';
          break;
        case 3:
          testClass = 'option';
          break;
        default:
          testClass = 'individual';
      }
      
      return testClass;
    },
    truncate(value) {
      var str = '';
      var val = value.replace(/(<([^>]+)>)/ig,' ');
      
      if (val.length > 160) {
        str = val.substring(0,160) + '...';
      } else {
        str = val;
      }
      
      return str;
    },
    originalPrice(tests) {
      var originalPrice = 0;
      
      tests.forEach(function(t){
        originalPrice += parseFloat(t.price);
      });
      
      return originalPrice.toFixed(2);
    }
  }
};
</script>